/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { useNavigate } from "react-router-dom";
import {
  GoogleAuthProvider,
  signInWithPopup,
  FacebookAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { auth } from "../firebase/FirebaseConfig";
import { toast } from "react-toastify";
import {
  SocialSignupLoginVerification,
  getChefPortalSettings,
} from "../core/_requests";
import { useAuth } from "../core/Auth";
import { saveFcmTokenToServer } from "../firebase/NotificationService";
import { useEffect, useState } from "react";

const SocialSignup = () => {
  const navigate = useNavigate();
  const { saveAuth, setCurrentUser, currentUser } = useAuth();
  const [settings, setSettings] = useState<{
    apple?: boolean;
    google?: boolean;
    facebook?: boolean;
  }>({});

  // API call to verify social login
  const handleVerification = async (data: any) => {
    return await SocialSignupLoginVerification(data);
  };

  // Main social login handler
  const handleSocialLogin = async (
    provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider
  ) => {
    try {
      const result = await signInWithPopup(auth, provider);
      const chef = result?.user;

      if (!chef) {
        toast.error("User information could not be retrieved.");
        return;
      }

      // Prepare data for backend verification
      const data = {
        email: chef?.providerData?.[0]?.email || chef?.email || "",
        name: chef?.providerData?.[0]?.displayName || chef?.displayName || "",
        mobileNumber:
          chef?.providerData?.[0]?.phoneNumber || chef?.phoneNumber || "",
        providerId: chef?.providerData?.[0]?.providerId || "",
        providerUid: chef?.providerData?.[0]?.uid || "",
        signupType: 2, // 2 = social signup
      };

      const verificationResponse = await handleVerification(data);

      if (verificationResponse?.status === true) {
        const verificationData = verificationResponse.data;
        const { uuid: chefId, verifyOtp } = verificationData;

        if (verificationResponse?.message) {
          toast.success(verificationResponse.message);
        }

        // Mobile verification required
        if (!verifyOtp) {
          localStorage.setItem("chefuuid", chefId);
          localStorage.setItem("signupType", "social");
          localStorage.setItem("chefdata", JSON.stringify(verificationData));
          localStorage.setItem("logInAttempt", "true");
          navigate("/otp-device", { replace: true });
          return;
        }

        // Already verified
        await saveFcmTokenToServer({
          userId: chefId,
          userType: "chef",
          appVersion: "1.0.0",
        });

        saveAuth(verificationData);
        setCurrentUser(verificationData);
        localStorage.setItem("manualToggle", JSON.stringify(false));
        navigate("/settings");
        window.location.reload();
      } else if (verificationResponse?.status === false) {
        // Handle email mismatch or errors
        if (verificationResponse?.isMatchedEmail === false) {
          toast.info(verificationResponse.message);
          localStorage.clear();
          navigate("/login");
        } else {
          localStorage.setItem("data", JSON.stringify(verificationResponse));
          toast.info(verificationResponse.message);
          navigate("/auth");
        }
      } else {
        toast.error("Verification failed. Please try again.");
      }
    } catch (error: any) {
      if (error.code === "auth/cancelled-popup-request") {
        // User canceled popup, do nothing
      } else {
        console.error("Error during social login:", error);
        toast.error("Something went wrong during the login process.");
      }
    }
  };

  // Fetch social login settings from backend
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getChefPortalSettings();
        if (res?.status && res?.data) {
          setSettings(res.data.social_login);
        }
      } catch (error) {
        console.error("Error fetching portal settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // Google, Facebook, Apple handlers
  const handleGoogle = () => {
    const provider = new GoogleAuthProvider();
    provider.addScope("email");
    provider.addScope("profile");
    handleSocialLogin(provider);
  };

  const handleFacebook = () => handleSocialLogin(new FacebookAuthProvider());

  const handleApple = () => {
    const appleProvider = new OAuthProvider("apple.com");
    appleProvider.addScope("email");
    appleProvider.addScope("name");
    handleSocialLogin(appleProvider);
  };

  return (
    <div className="d-flex w-100 align-items-center justify-content-center ss-ico">
      {settings.google && (
        <a className="btn" onClick={handleGoogle}>
          <img
            alt="Google Logo"
            src="/assets/images/google-icon.svg"
            className="ico google w-75"
          />
        </a>
      )}

      {settings.facebook && (
        <a className="btn" onClick={handleFacebook}>
          <img
            alt="Facebook Logo"
            src="/assets/images/facebook.png"
            className="ico fb w-70"
          />
        </a>
      )}

      {settings.apple && (
        <a className="btn" onClick={handleApple}>
          <img
            alt="Apple Logo"
            src="/assets/images/apple.png"
            className="ico w-70"
          />
        </a>
      )}
    </div>
  );
};

export { SocialSignup };
